import { Treatment } from '../types/content'

const PATCH =
  'https://cdn.magicpatterns.com/patterns/generated-images/7f8b64f0-5baa-4b05-9885-ea1361fa9f61.jpg'
const BOTTLE =
  'https://cdn.magicpatterns.com/patterns/generated-images/440aa554-4428-497d-98e7-ce193c3d9ba8.jpg'
const KIT =
  'https://cdn.magicpatterns.com/patterns/generated-images/1a7b07cf-ece3-4753-852c-205ec8dd1020.jpg'

export const treatments: Treatment[] = [
  {
    slug: 'estradiol-patch',
    name: 'Estradiol transdermal patch',
    category: 'Hormonal',
    status: 'FDA-approved',
    evidence:
      'The most effective treatment available for hot flushes and night sweats. Transdermal delivery avoids the clot risk associated with oral estrogen.',
    form: 'Adhesive patch, changed twice weekly',
    strength: '0.025 – 0.1 mg/day',
    price: 34,
    cadence: 'per month',
    priceNote: 'Included in the Hormone Therapy path at no additional cost.',
    image: PATCH,
    tagline: 'First-line for flushes, sweats and the sleep loss underneath them.',
    description:
      'Body-identical estradiol delivered through the skin at a steady rate. Because it bypasses first-pass liver metabolism, it does not carry the venous thrombosis risk associated with oral estrogen — which is why it is preferred where there is any clot history, migraine with aura, or elevated cardiovascular risk.',
    pharmacy: 'US-licensed retail pharmacy',
    carePath: 'hormone-therapy',
    rxRequired: true,
    highlights: [
      'Reduces hot flush frequency by around 75% in trials',
      'Steady delivery avoids the peaks and troughs of oral dosing',
      'No first-pass liver metabolism, so no added clot risk',
      'Requires nightly progesterone if you have a uterus',
    ],
    facts: [
      { label: 'Route', value: 'Transdermal' },
      { label: 'Frequency', value: 'Twice weekly' },
      { label: 'Titration', value: 'Reviewed at 6 and 12 weeks' },
      { label: 'Monitoring', value: 'Blood pressure, symptom score' },
    ],
  },
  {
    slug: 'micronised-progesterone',
    name: 'Micronised progesterone',
    category: 'Hormonal',
    status: 'FDA-approved',
    evidence:
      'Required for endometrial protection alongside estrogen. Independently useful for sleep and anxiety through its allopregnanolone metabolite.',
    form: 'Oral capsule, taken at night',
    strength: '100 – 200 mg',
    price: 22,
    cadence: 'per month',
    priceNote: 'Included in the Hormone Therapy and Sleep, Mood & Fog paths.',
    image: BOTTLE,
    tagline: 'The one that changes 3am.',
    description:
      'Body-identical progesterone, taken at night. It protects the uterine lining when you are taking estrogen, and it metabolises into allopregnanolone — which acts on the same receptors as anti-anxiety medication. For many women this is the single most noticeable prescription on this list within a fortnight.',
    pharmacy: 'US-licensed retail pharmacy',
    carePath: 'sleep-mood-fog',
    rxRequired: true,
    highlights: [
      'Protects the endometrium alongside estrogen therapy',
      'Sedating — taken at night deliberately, not incidentally',
      'Can be cycled or taken continuously depending on your stage',
      'Body-identical, not a synthetic progestin',
    ],
    facts: [
      { label: 'Route', value: 'Oral' },
      { label: 'Timing', value: 'At night, with food' },
      { label: 'Cycled or continuous', value: 'Depends on your stage' },
      { label: 'Monitoring', value: 'Bleeding pattern, sleep score' },
    ],
  },
  {
    slug: 'vaginal-estradiol',
    name: 'Vaginal estradiol cream',
    category: 'Local',
    status: 'FDA-approved',
    evidence:
      'Highly effective for genitourinary syndrome of menopause and for reducing recurrent urinary tract infections. Systemic absorption at standard dosing is minimal.',
    form: 'Cream with applicator',
    strength: '0.01%',
    price: 26,
    cadence: 'per month',
    priceNote: 'Included in the Intimacy & Vaginal Health path.',
    image: BOTTLE,
    tagline: 'The most under-prescribed treatment in women’s health.',
    description:
      'Applied locally, this restores the thickness, elasticity and natural lubrication of vulvovaginal tissue over eight to twelve weeks. It substantially reduces recurrent UTIs. It is appropriate for the overwhelming majority of women, and major bodies now support its use in many breast cancer survivors after discussion with their oncology team.',
    pharmacy: 'US-licensed retail pharmacy',
    carePath: 'intimacy-vaginal-health',
    rxRequired: true,
    highlights: [
      'Treats dryness, burning, painful sex, urgency and recurrent UTIs',
      'Minimal systemic absorption at standard dosing',
      'Loading dose for two weeks, then twice weekly',
      'Unlike hot flushes, this problem does not resolve on its own',
    ],
    facts: [
      { label: 'Route', value: 'Local vaginal' },
      { label: 'Frequency', value: 'Twice weekly after loading' },
      { label: 'Time to full effect', value: '8–12 weeks' },
      { label: 'Systemic effect', value: 'Minimal' },
    ],
  },
  {
    slug: 'fezolinetant',
    name: 'Fezolinetant',
    category: 'Non-hormonal',
    status: 'FDA-approved',
    evidence:
      'A neurokinin-3 receptor antagonist that targets the brain’s temperature control directly. Meaningful reduction in flush frequency without hormones.',
    form: 'Oral tablet, daily',
    strength: '45 mg',
    price: 148,
    cadence: 'per month',
    priceNote: 'Insurance coverage varies. We will tell you before you commit.',
    image: BOTTLE,
    tagline: 'For women who cannot, or choose not to, take hormones.',
    description:
      'The first genuinely new mechanism for vasomotor symptoms in decades. It blocks the neurokinin-3 signalling that drives the flush response, and it works whether or not you are a hormone therapy candidate. Liver function is checked before starting and periodically thereafter.',
    pharmacy: 'US-licensed retail pharmacy',
    carePath: 'hormone-therapy',
    rxRequired: true,
    highlights: [
      'No estrogen involved — appropriate after breast cancer',
      'Reduces both frequency and severity of flushes',
      'Requires baseline and periodic liver function testing',
      'Does not treat vaginal, bone or urinary symptoms',
    ],
    facts: [
      { label: 'Route', value: 'Oral' },
      { label: 'Frequency', value: 'Once daily' },
      { label: 'Monitoring', value: 'Liver function at 3, 6 and 9 months' },
      { label: 'Hormonal', value: 'No' },
    ],
  },
  {
    slug: 'testosterone-low-dose',
    name: 'Testosterone cream, low dose',
    category: 'Hormonal',
    status: 'Off-label for women in the US',
    evidence:
      'Good randomised evidence for distressing low sexual desire in post-menopausal women. Evidence for energy, mood and cognition is weak — we will not claim it.',
    form: 'Topical cream, daily',
    strength: 'Female physiological dosing',
    price: 38,
    cadence: 'per month',
    priceNote: 'Requires quarterly monitoring, included.',
    image: PATCH,
    tagline: 'Prescribed carefully, monitored properly, oversold nowhere.',
    description:
      'There is no FDA-approved testosterone product for women in the US, so this is prescribed off-label at female physiological doses — roughly a tenth of a male dose. The evidence supports it specifically for hypoactive sexual desire disorder after menopause. Claims about energy, mood, muscle and cognition in women are not well supported, and we will say so.',
    pharmacy: 'US-licensed 503A compounding pharmacy',
    carePath: 'intimacy-vaginal-health',
    rxRequired: true,
    highlights: [
      'Female dosing — approximately one tenth of male dosing',
      'Total and free testosterone monitored quarterly',
      'Prescribed after pain, sleep and mood have been addressed',
      'Not FDA-approved for women; compounded and dispensed in the US',
    ],
    facts: [
      { label: 'Route', value: 'Topical' },
      { label: 'Frequency', value: 'Daily' },
      { label: 'Monitoring', value: 'Levels at 3 months, then 6-monthly' },
      { label: 'Regulatory status', value: 'Off-label, compounded' },
    ],
  },
  {
    slug: 'ssri-vasomotor',
    name: 'Low-dose SSRI / SNRI',
    category: 'Non-hormonal',
    status: 'Some formulations FDA-approved for vasomotor symptoms',
    evidence:
      'Reduces hot flush frequency by roughly half at doses lower than those used for depression. Also treats mood where it is genuinely present.',
    form: 'Oral, daily',
    strength: 'Low dose',
    price: 18,
    cadence: 'per month',
    priceNote: 'Generic. Among the least expensive options here.',
    image: BOTTLE,
    tagline: 'A legitimate option — but not a substitute for asking about your hormones.',
    description:
      'Used at low doses, certain SSRIs and SNRIs reduce vasomotor symptoms independently of any antidepressant effect. That makes them a real option for women who cannot take estrogen. What is not acceptable is prescribing one instead of considering hormones — which is what happens to a great many women, and why this page exists.',
    pharmacy: 'US-licensed retail pharmacy',
    carePath: 'sleep-mood-fog',
    rxRequired: true,
    highlights: [
      'Around 50% reduction in flush frequency in trials',
      'Appropriate after breast cancer, with interaction checks',
      'Treats co-occurring depression where it is present',
      'Does not protect bone, tissue or urinary health',
    ],
    facts: [
      { label: 'Route', value: 'Oral' },
      { label: 'Frequency', value: 'Once daily' },
      { label: 'Onset', value: '2–4 weeks' },
      { label: 'Hormonal', value: 'No' },
    ],
  },
  {
    slug: 'vaginal-moisturiser',
    name: 'Vaginal moisturiser',
    category: 'Supportive',
    status: 'Over the counter, no prescription',
    evidence:
      'Improves comfort and tissue hydration. Does not restore tissue the way local estrogen does, and is not a replacement for it.',
    form: 'Non-hormonal gel',
    strength: 'Applied every 2–3 days',
    price: 16,
    cadence: 'per month',
    priceNote: 'No prescription and no membership required for this one.',
    image: KIT,
    tagline: 'Different from a lubricant — and most women are only told about lubricant.',
    description:
      'A moisturiser is used regularly to hydrate tissue over time. A lubricant is used during sex to reduce friction. They do different jobs and you want both. Neither reverses tissue thinning, which is what local estrogen is for.',
    pharmacy: 'Shipped directly',
    carePath: 'intimacy-vaginal-health',
    rxRequired: false,
    highlights: [
      'No prescription needed',
      'Used every two to three days, not only during sex',
      'Safe alongside local estrogen',
      'Choose pH-balanced, low-osmolality formulations',
    ],
    facts: [
      { label: 'Route', value: 'Local vaginal' },
      { label: 'Frequency', value: 'Every 2–3 days' },
      { label: 'Prescription', value: 'Not required' },
      { label: 'Hormonal', value: 'No' },
    ],
  },
  {
    slug: 'vitamin-d3-k2',
    name: 'Vitamin D3 + K2',
    category: 'Supportive',
    status: 'Supplement, dosed to your measured level',
    evidence:
      'Correcting genuine deficiency supports bone health and muscle function. Supplementing above sufficiency does not confer additional benefit.',
    form: 'Oral capsule, daily',
    strength: 'Dosed to serum level',
    price: 14,
    cadence: 'per month',
    priceNote: 'Only prescribed where your level actually warrants it.',
    image: KIT,
    tagline: 'Repleted to a number, not handed out by default.',
    description:
      'Deficiency is common, easily measured and cheap to fix, and it contributes to bone loss and muscle weakness in exactly the decade you can least afford them. We test first. If you are already sufficient, we will tell you that you do not need this.',
    pharmacy: 'Shipped directly',
    carePath: 'bones-heart-long-view',
    rxRequired: false,
    highlights: [
      'Dose set by your measured serum 25-OH vitamin D',
      'K2 supports calcium deposition into bone',
      'Retested at three months',
      'Not recommended if you are already sufficient',
    ],
    facts: [
      { label: 'Route', value: 'Oral' },
      { label: 'Frequency', value: 'Daily' },
      { label: 'Testing', value: 'Baseline and 3 months' },
      { label: 'Prescription', value: 'Not required' },
    ],
  },
  {
    slug: 'tirzepatide',
    name: 'Tirzepatide',
    category: 'Non-hormonal',
    status: 'FDA-approved',
    evidence:
      'Substantial evidence for weight and metabolic outcomes. Accelerates muscle loss without adequate protein and resistance training — which matters more in midlife than at any other stage.',
    form: 'Weekly subcutaneous injection',
    strength: '2.5 – 15 mg, titrated',
    price: 279,
    cadence: 'per month',
    priceNote: 'Price held for twelve months. No hidden titration surcharges.',
    image: KIT,
    tagline: 'Prescribed where indicated — never as a first move.',
    description:
      'We prescribe GLP-1 medication where there is a clinical indication, and always alongside a resistance training and protein plan, because muscle preservation is the whole game in midlife. We will not prescribe one to a woman whose actual problem is untreated sleep loss or untreated perimenopause.',
    pharmacy: 'US-licensed retail pharmacy',
    carePath: 'midlife-metabolism',
    rxRequired: true,
    highlights: [
      'Prescribed only alongside a muscle-preservation plan',
      'Titrated slowly to limit gastrointestinal effects',
      'Metabolic markers reviewed quarterly, not just weight',
      'Discontinuation plan discussed before you start',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous' },
      { label: 'Frequency', value: 'Weekly' },
      { label: 'Titration', value: 'Monthly steps' },
      { label: 'Monitoring', value: 'HbA1c, lipids, strength' },
    ],
  },
]

export function getTreatment(slug: string): Treatment | undefined {
  return treatments.find((treatment) => treatment.slug === slug)
}

export const treatmentCategories = [
  'All',
  'Hormonal',
  'Non-hormonal',
  'Local',
  'Supportive',
] as const
